// TV display. Open as /display/?screen=<code>   (e.g. ?screen=hut1)
// Optional: &at=2026-10-05T14:30  → pretend it's that time (for testing specials / lunch→dinner).
//           &orientation=vertical-right|vertical-left|horizontal  → try another orientation without saving it
//           &upright  → show a vertical TV standing up in a browser window instead of turned (admin preview)
import { select, version as fetchVersion, heartbeat, onDemoChange, DEMO } from './tvdata.js';
import {
  localNow, currentPeriod, itemVisible, priceFor, money, fmtTime, liveUntilLabel,
} from '../shared/pricing.js';
import { dishPhoto, categoryPhotos, periodPhoto } from '../shared/photos.js';

const params = new URLSearchParams(location.search);
const code = (params.get('screen') || '').trim().toLowerCase();
const simulatedAt = params.get('at');
const clockOffset = simulatedAt ? Date.parse(simulatedAt) - Date.now() : 0;
const clock = () => new Date(Date.now() + clockOffset);
const orientationOverride = params.get('orientation');
const upright = params.has('upright');

const TICK_MS = 15_000;            // re-evaluate specials / lunch→dinner
const VERSION_POLL_MS = 20_000;    // how often TVs check for changes (a tiny request)
const HEARTBEAT_MS = 60_000;
const RELOAD_MS = 6 * 3600_000;    // pick up new display code a few times a day

const app = document.getElementById('app');
const CACHE_KEY = `tm-cache-${code}`;
let data = null;
let version = null;
let online = true;
let lastKey = '';
let slideTimer = null;
let slideIndex = 0;
let slideIds = [];
let footerTimer = null;
let frameH = innerHeight;          // height of the menu itself (the TV's width on a vertical TV)

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const bySort = (a, b) => (a.sort ?? 0) - (b.sort ?? 0) || String(a.name).localeCompare(String(b.name));

if (!code) showPicker();
else start();

// ───────────────────────── Data ─────────────────────────

async function fetchAll() {
  const [screen] = await select('screens', { code });
  if (!screen) {
    const err = new Error(`No screen is set up with code “${code}”.`);
    err.fatal = true;
    throw err;
  }
  const rid = screen.restaurant_id;
  const [restaurants, categories, items, rules, photos] = await Promise.all([
    select('restaurants', { id: rid }),
    select('categories', { restaurant_id: rid }),
    select('items', { restaurant_id: rid }),
    select('price_rules', { restaurant_id: rid }),
    fetch('../photos/photos.json').then((r) => (r.ok ? r.json() : null)).catch(() => null),
  ]);
  // Dish photo library (web/photos/photos.json). If it can't be read, keep the copy we already have.
  return { screen, restaurant: restaurants[0], categories, items, rules, photos: photos || (data && data.photos) || null };
}

async function refresh() {
  try {
    data = await fetchAll();
    online = true;
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* storage full: fine */ }
  } catch (e) {
    if (e.fatal) return showMessage('Screen not found', `${esc(e.message)}<br>Check the code in the admin → Screens.`);
    console.warn('refresh failed', e);
    online = false;
    if (!data) {
      try { data = JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { data = null; }
    }
    if (!data) {
      showMessage('Waiting for connection…', 'The menu will appear as soon as the internet is back.');
      setTimeout(refresh, 15_000);
      return;
    }
  }
  render(true);
}

let refreshTimer = null;
const refreshSoon = () => {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(refresh, 800);   // several changes in a row → one download
};

async function checkVersion() {
  if (!data) return;
  try {
    const v = await fetchVersion(data.screen.restaurant_id);
    if (version !== null && v !== version) refreshSoon();
    version = v;
    if (!online) refreshSoon();
  } catch {
    online = false;
    renderStatus();
  }
}

async function start() {
  await refresh();
  if (data) version = await fetchVersion(data.screen.restaurant_id).catch(() => null);
  onDemoChange(refreshSoon);
  setInterval(() => render(false), TICK_MS);
  setInterval(checkVersion, VERSION_POLL_MS);
  const beat = () => heartbeat(code, `${innerWidth}x${innerHeight} ${navigator.userAgent.slice(0, 120)}`).catch(() => {});
  beat();
  setInterval(beat, HEARTBEAT_MS);
  if (!window.TM_SNAPSHOT) {
    setTimeout(() => { if (navigator.onLine) location.reload(); }, RELOAD_MS);
    if (online && data) saveOfflineCopy();
  }
  window.addEventListener('online', refreshSoon);
}

/**
 * Fire TV app only: hand the app a complete, self-contained copy of this page (styles + code inline).
 * If the TV later starts without internet, the app opens this copy; it reads the last menu saved in
 * this browser, keeps switching specials on time, and reconnects by itself when the internet is back.
 */
async function saveOfflineCopy() {
  if (!window.MenuApp || !window.MenuApp.saveOfflineCopy) return;
  try {
    const [css, js] = await Promise.all(['display.css', 'display.bundle.js'].map((u) => fetch(u).then((r) => r.text())));
    const safe = (s) => s.replace(/<\/script/gi, '<\\/script');
    const html = '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">'
      + '<style>' + css + '</style></head><body><div id="app"></div>'
      + '<script>window.TM_SNAPSHOT=true;window.TM_CONFIG=' + safe(JSON.stringify(window.TM_CONFIG || {})) + ';</script>'
      + '<script>' + safe(js) + '</script></body></html>';
    window.MenuApp.saveOfflineCopy(html);
  } catch (e) {
    console.warn('offline copy not saved', e);
  }
}

// ───────────────────────── View model ─────────────────────────

function viewModel() {
  const { screen, restaurant, categories, items, rules, photos } = data;
  const now = localNow(restaurant.timezone || 'America/Los_Angeles', clock());
  const period = currentPeriod(restaurant, now);
  const price = (i) => {
    const p = priceFor(i, rules, now);
    return { ...i, ...p, until: p.rule ? liveUntilLabel(p.rule, now) : '', photo: dishPhoto(i, photos) };
  };

  const cats = screen.category_ids && screen.category_ids.length
    ? screen.category_ids.map((id) => categories.find((c) => c.id === id)).filter(Boolean)
    : [...categories].sort(bySort);

  const sections = cats
    .map((c) => ({
      id: c.id,
      name: c.name,
      photos: categoryPhotos(c, photos),
      items: oncePerSection(items.filter((i) => i.category_id === c.id && itemVisible(i, period)).sort(bySort).map(price)),
    }))
    .filter((s) => s.items.length);

  const catName = {};
  categories.forEach((c) => { catName[c.id] = c.name; });
  const specials = items
    .filter((i) => itemVisible(i, period))
    .map(price)
    .filter((i) => i.rule)
    .map((i) => ({ ...i, category: catName[i.category_id] }));

  return {
    now, period, sections, specials, restaurant, screen,
    vertical: orientationOf(screen) !== 'horizontal',
    periodPhoto: periodPhoto(period, photos),
  };
}

/** An automatic library photo shows once per category (Vegetable and Shrimp Pakora don't both get the pakora photo). */
function oncePerSection(list) {
  const used = new Set();
  return list.map((i) => {
    if (!i.photo) return i;
    if (i.photo.auto && used.has(i.photo.full)) return { ...i, photo: null };
    used.add(i.photo.full);
    return i;
  });
}

// ───────────────────────── Rendering ─────────────────────────

function render(force) {
  if (!data) return;
  const vm = viewModel();
  applyTheme(vm.restaurant.accent);
  applyOrientation(vm.screen);
  // Only touch the DOM when something visible changed (avoids flicker on the TV)
  const key = JSON.stringify([
    vm.period, vm.screen, vm.restaurant, orientationOf(vm.screen),
    vm.sections.map((s) => [s.name, s.photos.map((p) => p.full), s.items.map((i) => [i.id, i.name, i.description, i.price, i.original, i.photo && i.photo.full, i.is_veg, i.spicy, i.until])]),
    vm.periodPhoto && vm.periodPhoto.full,
    vm.specials.map((i) => [i.id, i.price, i.until]),
  ]);
  if (force || key !== lastKey) {
    lastKey = key;
    if (vm.screen.mode === 'slideshow') renderSlideshow(vm);
    else if (vm.screen.mode === 'showcase') renderShowcase(vm);
    else renderMenu(vm);
  }
  renderClock(vm);
  renderStatus();
}

function applyTheme(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return;
  const n = parseInt(m[1], 16);
  const rgb = `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
  const s = document.documentElement.style;
  s.setProperty('--accent', `#${m[1]}`);
  for (const a of [6, 14, 16, 20, 22, 30, 45]) s.setProperty(`--a${String(a).padStart(2, '0')}`, `rgba(${rgb}, ${a / 100})`);
}

function orientationOf(screen) {
  const o = orientationOverride || screen.orientation;
  return o === 'vertical-right' || o === 'vertical-left' ? o : 'horizontal';
}

/**
 * Fire TV sticks always send a landscape picture, even when the TV hangs on its side.
 * For a vertical TV we turn the whole page a quarter turn so the menu stands upright on the wall,
 * and tell the CSS (--vw / --vh) how wide and tall the menu now is.
 */
function applyOrientation(screen) {
  const o = orientationOf(screen);
  const W = innerWidth;
  const H = innerHeight;
  let w = W;
  let h = H;
  let cls = '';
  let css = '';
  if (o !== 'horizontal' && W > H) {
    if (upright) {
      // Preview in a normal browser window: a tall 9:16 TV in the middle of the window
      h = H; w = Math.round(H * 9 / 16);
      cls = 'framed';
      css = `width:${w}px;height:${h}px`;
    } else {
      w = H; h = W;
      cls = 'turned';
      css = `width:${w}px;height:${h}px;transform:translate(-50%,-50%) rotate(${o === 'vertical-right' ? 90 : -90}deg)`;
    }
  }
  // (If the window is already tall, e.g. a portrait monitor, the menu simply fills it.)
  if (app.className !== cls) app.className = cls;
  if (app.getAttribute('style') !== css) app.setAttribute('style', css);
  const root = document.documentElement.style;
  root.setProperty('--vw', `${w / 100}px`);
  root.setProperty('--vh', `${h / 100}px`);
  frameH = h;
}

function brandHtml(r) {
  return `
    <div class="brand">
      ${r.logo_url ? `<img src="${esc(r.logo_url)}" alt="">` : ''}
      <div><h1>${esc(r.name)}</h1>${r.area ? `<p>${esc(r.area)}</p>` : ''}</div>
    </div>`;
}

/** Background logo strength (percent, set in the admin) as an opacity. */
function bgStrength(r) {
  return Math.max(2, Math.min(30, Number(r.bg_logo_strength) || 7)) / 100;
}

/** The restaurant's faint gold logo behind the menu (the admin already made the image gold). */
function watermarkHtml(r) {
  if (!r.bg_logo_url) return '';
  return `<div class="bg-logo" style="background-image:url('${esc(r.bg_logo_url)}');opacity:${bgStrength(r)}"></div>`;
}

function periodHtml(vm) {
  const label = vm.period === 'lunch' ? 'Lunch Menu' : 'Dinner Menu';
  const extra = vm.period === 'lunch' ? `<small>until ${fmtTime(vm.restaurant.lunch_end)}</small>` : '';
  return `<span class="period">${label}${extra}</span>`;
}

function badges(i) {
  return `${i.is_veg ? '<span class="veg" title="Vegetarian"></span>' : ''}${'<span class="chili"></span>'.repeat(i.spicy || 0)}`;
}

function priceHtml(i) {
  return i.rule
    ? `<span class="was">${money(i.original)}</span>${money(i.price)}`
    : money(i.price);
}

function itemHtml(i, showImages) {
  const tag = i.rule ? `<span class="tag">Special${i.until ? ` · ${esc(i.until)}` : ''}</span>` : '';
  const body = `
    <div class="row"><span class="name">${esc(i.name)}${badges(i)}</span><span class="dots"></span><span class="price">${priceHtml(i)}</span></div>
    ${i.description ? `<p class="desc">${esc(i.description)}</p>` : ''}
    ${tag}`;
  if (!showImages) return `<div class="item${i.rule ? ' special' : ''}">${body}</div>`;
  const thumb = i.photo
    ? `<img class="thumb" src="${esc(i.photo.thumb)}" alt="">`
    : `<span class="thumb none">${esc(i.name.trim()[0] || '')}</span>`;
  return `<div class="item with-img${i.rule ? ' special' : ''}">${thumb}<div>${body}</div></div>`;
}

/** Option 2: the category heading on a strip of 1–3 photos (Appetizers → samosa, kachori, pakora). */
function categoryHeadHtml(s, withPhotos) {
  const pics = withPhotos ? s.photos.slice(0, 3) : [];
  if (!pics.length) return `<h2>${esc(s.name)}</h2>`;
  return `
    <div class="cat-banner n${pics.length}">
      <div class="pics">${pics.map((p) => `<span style="background-image:url('${esc(p.full)}')"></span>`).join('')}</div>
      <h2>${esc(s.name)}</h2>
    </div>`;
}

function specialsFooter(vm) {
  if (!vm.specials.length) return '';
  const list = vm.specials
    .map((i) => `<span class="sp"><b>${esc(i.name)}</b> ${money(i.price)}${i.until ? ` <span style="opacity:.75">(${esc(i.until)})</span>` : ''}</span>`)
    .join('<span class="sep">◆</span>');
  return `<footer class="specials"><span class="label">Today’s Specials</span><span class="list">${list}</span></footer>`;
}

function renderMenu(vm) {
  stopSlideshow();
  // A vertical TV is narrow: more than 2 columns would squeeze every dish name onto two lines
  const cols = Math.max(1, Math.min(vm.vertical ? 2 : 5, Number(vm.screen.columns) || 3));
  const sections = vm.sections.map((s) => `
      <section class="cat">
        ${categoryHeadHtml(s, vm.screen.category_photos)}
        ${s.items.map((i) => itemHtml(i, vm.screen.show_images)).join('')}
      </section>`).join('');
  app.innerHTML = `
    <div class="screen menu${vm.vertical ? ' vertical' : ''}">
      ${watermarkHtml(vm.restaurant)}
      <header class="top">${brandHtml(vm.restaurant)}<div class="meta">${periodHtml(vm)}<span class="clock"></span></div></header>
      <main class="board"><div class="cols" style="--cols:${cols}">${sections || '<p class="desc">No items to show right now.</p>'}</div></main>
      ${specialsFooter(vm)}
    </div>`;
  fitText();
  rotateFooterIfCrowded();
}

/** If the specials don't fit on one line, show them one at a time instead of cutting them off. */
function rotateFooterIfCrowded() {
  clearInterval(footerTimer);
  const list = app.querySelector('.specials .list');
  if (!list || list.scrollWidth <= list.clientWidth + 2) return;
  const items = [...list.querySelectorAll('.sp')];
  list.querySelectorAll('.sep').forEach((s) => s.remove());
  let k = 0;
  const show = () => { items.forEach((el, n) => { el.style.display = n === k ? '' : 'none'; }); k = (k + 1) % items.length; };
  show();
  footerTimer = setInterval(show, 5000);
}

/** Biggest font size at which the whole menu fits on screen without scrolling. */
function fitText() {
  const board = app.querySelector('.board');
  const cols = app.querySelector('.cols');
  if (!board || !cols) return;
  const rows = [...cols.querySelectorAll('.row')];
  // Measure the menu as laid out, not as turned on a vertical TV (restored before the TV redraws)
  // TV screens → "Category start": optionally every category that would spill over starts a new column
  const together = !!data.screen.category_columns;
  const turn = app.style.transform;
  app.style.transform = 'none';
  const fits = () => {
    if (together) keepCategoriesTogether(cols);
    return cols.scrollWidth <= cols.clientWidth + 2 && cols.scrollHeight <= cols.clientHeight + 2;
  };
  // Dish names on one line look much tidier (matters most on narrow vertical TVs)
  const oneLine = (fs) => rows.every((r) => r.offsetHeight < fs * 1.8);
  const largest = (ok) => {
    let lo = frameH * 0.009;
    let hi = frameH * 0.05;
    for (let k = 0; k < 12; k++) {
      const mid = (lo + hi) / 2;
      board.style.setProperty('--fs', `${mid}px`);
      if (ok(mid)) lo = mid; else hi = mid;
    }
    return lo;
  };
  const any = largest(fits);
  const tidy = largest((fs) => fits() && oneLine(fs));
  // ...unless that would make the text a lot smaller (very long names, or few columns)
  board.style.setProperty('--fs', `${tidy >= any * 0.75 ? tidy : any}px`);
  if (together) keepCategoriesTogether(cols);
  app.style.transform = turn;
}

/**
 * "Start each category at the top of a column": a category never starts at the bottom of one column and carries on in the next: if it doesn't fit
 * under the category above it, it moves to the top of the next column (a long one may still continue
 * into the column after that).
 */
function keepCategoriesTogether(cols) {
  const cats = [...cols.querySelectorAll('.cat')];
  cats.forEach((c) => c.classList.remove('new-col'));
  const top = cols.getBoundingClientRect().top;
  for (const cat of cats) {
    const first = cat.firstElementChild.getBoundingClientRect();
    const last = cat.lastElementChild.getBoundingClientRect();
    const split = Math.abs(last.left - first.left) > 2;
    const atTop = first.top - top < 2;
    if (split && !atTop) cat.classList.add('new-col');   // later categories are measured after this moves
  }
}

// ── Slideshow ──

function renderSlideshow(vm) {
  const all = [].concat(...vm.sections.map((s) => s.items.map((i) => ({ ...i, category: s.name }))));
  const withPhotos = all.filter((i) => i.photo);
  // Specials always go first; if photos exist, plain items without a photo are skipped
  const pool = withPhotos.length ? all.filter((i) => i.photo || i.rule) : all;
  const slides = [...pool.filter((i) => i.rule), ...pool.filter((i) => !i.rule)];

  const prevId = slideIds[slideIndex];
  slideIds = slides.map((s) => s.id);
  slideIndex = Math.max(0, slideIds.indexOf(prevId));

  app.innerHTML = `
    <div class="screen slideshow${vm.vertical ? ' vertical' : ''}">
      <div class="slide-brand">${brandHtml(vm.restaurant)}</div>
      <div class="slide-meta meta">${periodHtml(vm)}<span class="clock"></span></div>
      ${slides.map((i) => slideHtml(i, vm)).join('') || '<div class="message"><p>No items to show right now.</p></div>'}
      <div class="progress"></div>
    </div>`;
  stopSlideshow();
  if (!slides.length) return;
  showSlide(slideIndex);
  if (slides.length > 1) {
    const secs = Math.max(3, Number(vm.screen.slide_seconds) || 8);
    slideTimer = setInterval(() => showSlide((slideIndex + 1) % slides.length), secs * 1000);
  }
}

// ── Photo showcase (Option 3): a lunch / dinner photo, then one photo collage per category ──

function renderShowcase(vm) {
  const r = vm.restaurant;
  const slides = [];
  if (vm.periodPhoto) {
    slides.push({
      id: `period-${vm.period}`,
      html: `
        <div class="slide hero" style="background-image:url('${esc(vm.periodPhoto.full)}')">
          <div class="hero-cap">
            <p class="kicker">${esc(r.name)}${r.area ? ` · ${esc(r.area)}` : ''}</p>
            <h2>${vm.period === 'lunch' ? 'Lunch Menu' : 'Dinner Menu'}</h2>
            ${vm.period === 'lunch' ? `<p class="sub">Served until ${fmtTime(r.lunch_end)}</p>` : ''}
          </div>
        </div>`,
    });
  }
  for (const s of vm.sections) {
    // The category's own dishes first (each photo once), topped up with the category's photos
    const tiles = [];
    const seen = new Set();
    for (const i of s.items) {
      if (i.photo && !seen.has(i.photo.full)) { seen.add(i.photo.full); tiles.push({ url: i.photo.full, label: i.name }); }
    }
    for (const p of s.photos) {
      if (tiles.length >= 3) break;
      if (!seen.has(p.full)) { seen.add(p.full); tiles.push({ url: p.full, label: '' }); }
    }
    if (!tiles.length) continue;
    const shown = tiles.slice(0, vm.vertical ? 4 : 6);
    slides.push({
      id: `cat-${s.id}`,
      html: `
        <div class="slide collage n${shown.length}">
          <div class="tiles">${shown.map((t) => `
            <figure style="background-image:url('${esc(t.url)}')">${t.label ? `<figcaption>${esc(t.label)}</figcaption>` : ''}</figure>`).join('')}
          </div>
          <div class="collage-cap">
            <p class="kicker">${esc(r.name)}</p>
            <h2>${esc(s.name)}</h2>
            <p class="sub">${s.items.length} dish${s.items.length === 1 ? '' : 'es'}</p>
          </div>
        </div>`,
    });
  }

  const prevId = slideIds[slideIndex];
  slideIds = slides.map((x) => x.id);
  slideIndex = Math.max(0, slideIds.indexOf(prevId));

  app.innerHTML = `
    <div class="screen slideshow showcase${vm.vertical ? ' vertical' : ''}">
      ${slides.map((x) => x.html).join('') || '<div class="message"><p>No photos to show right now.</p></div>'}
      <div class="slide-meta meta">${periodHtml(vm)}<span class="clock"></span></div>
      <div class="progress"></div>
    </div>`;
  stopSlideshow();
  if (!slides.length) return;
  showSlide(slideIndex);
  if (slides.length > 1) {
    const secs = Math.max(3, Number(vm.screen.slide_seconds) || 8);
    slideTimer = setInterval(() => showSlide((slideIndex + 1) % slides.length), secs * 1000);
  }
}

function slideHtml(i, vm) {
  const bg = i.photo ? ` style="background-image:url('${esc(i.photo.full)}')"` : '';
  const r = vm.restaurant;
  // Slides without a photo show the gold logo on the right (or the dish's first letter if there's no logo)
  const art = r.bg_logo_url
    ? `<div class="slide-logo" style="background-image:url('${esc(r.bg_logo_url)}');opacity:${Math.min(0.4, bgStrength(r) * 3)}"></div>`
    : `<div class="initial">${esc(i.name.trim()[0] || '')}</div>`;
  return `
    <div class="slide ${i.photo ? 'photo' : 'plain'}"${bg}>
      ${i.photo ? '' : art}
      <div class="caption">
        <p class="kicker">${esc(i.category)}</p>
        <h2>${esc(i.name)}${badges(i)}</h2>
        ${i.description ? `<p class="desc">${esc(i.description)}</p>` : ''}
        <div class="bigprice">${i.rule ? `<span class="was">${money(i.original)}</span>` : ''}<span>${money(i.price)}</span></div>
        ${i.rule ? `<span class="tag">Today’s Special${i.until ? ` · ${esc(i.until)}` : ''}</span>` : ''}
      </div>
    </div>`;
}

function showSlide(n) {
  const slides = app.querySelectorAll('.slide');
  slides.forEach((s, k) => s.classList.toggle('on', k === n));
  slideIndex = n;
  const bar = app.querySelector('.progress');
  if (bar && slides.length > 1) {
    const secs = Math.max(3, Number(data.screen.slide_seconds) || 8);
    bar.classList.remove('run');
    bar.style.transitionDuration = '0s';
    void bar.offsetWidth;  // restart the animation
    bar.style.transitionDuration = `${secs}s`;
    bar.classList.add('run');
  }
}

function stopSlideshow() {
  clearInterval(slideTimer);
  slideTimer = null;
}

// ── Clock, status, messages ──

function renderClock(vm) {
  const t = new Intl.DateTimeFormat('en-US', { timeZone: vm.now.tz, hour: 'numeric', minute: '2-digit' }).format(vm.now.instant);
  app.querySelectorAll('.clock').forEach((el) => { el.textContent = t; });
}

function renderStatus() {
  let dot = document.querySelector('.offline');
  if (!online && !dot) {
    dot = document.createElement('div');
    dot.className = 'offline';
    dot.title = 'Offline – showing the last saved menu';
    document.body.appendChild(dot);
  } else if (online && dot) dot.remove();

  if (simulatedAt && !document.querySelector('.sim')) {
    const b = document.createElement('div');
    b.className = 'sim';
    b.textContent = `Preview: simulated time ${simulatedAt.replace('T', ' ')}${DEMO ? ' · demo data' : ''}`;
    document.body.appendChild(b);
  }
}

function showMessage(title, html) {
  stopSlideshow();
  app.innerHTML = `<div class="message"><h1>${esc(title)}</h1><p>${html}</p></div>`;
}

async function showPicker() {
  document.body.style.overflow = 'auto';
  const [rs, ss] = await Promise.all([select('restaurants'), select('screens')]);
  rs.sort(bySort);
  app.innerHTML = `
    <div class="picker">
      <h1>Choose a screen</h1>
      <p>Each TV opens this page with its own code, e.g. <code>?screen=hut1</code>.${DEMO ? ' (Demo data)' : ''}</p>
      <div class="grid">
        ${rs.map((r) => `
          <div class="card">
            <h2>${esc(r.name)}</h2><p>${esc(r.area || '')}</p>
            ${ss.filter((s) => s.restaurant_id === r.id).sort(bySort)
              .map((s) => `<a href="?screen=${encodeURIComponent(s.code)}${DEMO && params.has('demo') ? '&demo' : ''}">${esc(s.name)} <code>${esc(s.code)}</code></a>`).join('')}
          </div>`).join('')}
      </div>
    </div>`;
}

window.addEventListener('resize', () => {
  if (!data) return;
  applyOrientation(data.screen);
  if (data.screen.mode !== 'slideshow' && data.screen.mode !== 'showcase') fitText();
});
